import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Controller, useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Calendar1, FileText, ListChecks, MapPin } from 'lucide-react';
import { useCreateJob, useJob, useUpdateJob } from '@/lib/api/hooks/job';
import { showToast } from '@/lib/toast';
import { ChevronLeftIcon } from '@/components/icons';
import { Button, CustomSelect, RichTextEditor } from '@/components/ui';
import Calendar from '@/components/ui/Calendar/Calendar';
import Input from '@/components/ui/Input/Input';
import { Heading, Paragraph } from '@/components/ui/Typography';
import jobTypesOptions from '@/utils/constants/jobTypesOptions';

const toDateOnly = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};

const getTodayDate = (): string => toDateOnly(new Date());

const parseDateOnly = (value: string): Date | null => {
  if (!value) return null;

  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;

  return new Date(year, month - 1, day);
};

const JobFormSchema = z
  .object({
    jobTitle: z.string().trim().min(1, 'Job title is required').max(120, 'Job title must be 120 characters or fewer'),
    jobLocation: z.string().trim().min(1, 'Location is required'),
    jobType: z.string().trim().min(1, 'Job type is required'),
    startDate: z.string().trim().min(1, 'Start date is required'),
    endDate: z.string().trim().min(1, 'End date is required'),
    description: z.string().optional(),
    // submissionEmail: z.string().trim().min(1, 'Submission email is required').email('Enter a valid email address')
  })
  .superRefine((data, ctx) => {
    const today = getTodayDate();

    if (data.startDate && data.startDate < today) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['startDate'],
        message: 'Start date cannot be in the past'
      });
    }

    if (data.endDate && data.endDate < today) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['endDate'],
        message: 'End date cannot be in the past'
      });
    }

    if (data.startDate && data.endDate && data.endDate < data.startDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['endDate'],
        message: 'End date cannot be before start date'
      });
    }
  });

type JobFormData = z.infer<typeof JobFormSchema>;

const DEFAULT_VALUES: JobFormData = {
  jobTitle: '',
  jobLocation: '',
  jobType: '',
  startDate: '',
  endDate: '',
  description: '',
  // submissionEmail: ''
};

const SectionHeader = ({
  icon,
  title
}: {
  icon: React.ReactNode;
  title: string;
}) => (
  <div className='mb-5 flex items-center gap-3'>
    <div className='flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-[#F8EFF2] text-wine-100'>
      {icon}
    </div>
    <Heading level={3} className='text-lg font-bold text-gray-900'>
      {title}
    </Heading>
  </div>
);

const FieldHint = ({ children }: { children: React.ReactNode }) => (
  <Paragraph className='mt-1 !text-sm text-gray-500'>{children}</Paragraph>
);

const JobEditor = () => {
  const { jobId } = useParams<{ jobId?: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const isEditMode = Boolean(jobId);
  const existingJob = location.state?.job;

  const [isDraftSaving, setIsDraftSaving] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);

  const { data: jobData, isLoading } = useJob(jobId);
  const createMutation = useCreateJob();
  const updateMutation = useUpdateJob();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting }
  } = useForm<JobFormData>({
    defaultValues: DEFAULT_VALUES,
    resolver: zodResolver(JobFormSchema),
    mode: 'all'
  });

  const watchTitle = useWatch({ control, name: 'jobTitle' });

  const jobTypeOptions = useMemo(() => jobTypesOptions, []);
  const isBusy = isSubmitting || isDraftSaving || isPublishing;

  useEffect(() => {
    if (!(isEditMode && (jobData?.data || existingJob))) {
      return;
    }

    const job = jobData?.data || existingJob;

    reset({
      jobTitle: job.jobTitle || '',
      jobLocation: job.jobLocation || '',
      jobType: job.jobType || '',
      startDate: job.startDate ? String(job.startDate).slice(0, 10) : '',
      endDate: job.endDate ? String(job.endDate).slice(0, 10) : '',
      description: job.description || '',
      // submissionEmail: (job.submissionEmail || '').replace(/^mailto:/i, '')
    });
  }, [isEditMode, jobData, existingJob, reset]);

  const submitWithStatus = async (data: JobFormData, status: 'DRAFT' | 'PUBLISH') => {
    const setLoading = status === 'DRAFT' ? setIsDraftSaving : setIsPublishing;
    setLoading(true);

    try {
      const payload = {
        jobTitle: data.jobTitle.trim(),
        jobLocation: data.jobLocation.trim(),
        jobType: data.jobType,
        startDate: data.startDate,
        endDate: data.endDate,
        description: data.description || '',
        // submissionEmail: data.submissionEmail.trim(),
        status
      };

      if (isEditMode && jobId) {
        const result = await updateMutation.mutateAsync({
          jobId,
          data: payload
        });

        if (!result.success) {
          throw new Error('Update failed');
        }

        showToast.success(status === 'PUBLISH' ? 'Job published successfully' : 'Job saved as draft successfully');
      } else {
        const result = await createMutation.mutateAsync(payload);

        if (!result.success) {
          throw new Error('Creation failed');
        }

        showToast.success(status === 'PUBLISH' ? 'Job published successfully' : 'Job created as draft successfully');
      }

      navigate('/admin/jobs');
    } catch (error) {
      console.error('Failed to save job:', error);
      showToast.error(error instanceof Error ? error.message : 'Failed to save job');
    } finally {
      setLoading(false);
    }
  };

  if (isLoading && jobId) {
    return (
      <div className='rounded-xl border border-gray-200 bg-white p-10 shadow-sm'>
        <div className='text-center'>
          <div className='inline-block h-8 w-8 animate-spin rounded-full border-b-2 border-wine-100' />
          <p className='mt-4 text-sm text-gray-500'>Loading job...</p>
        </div>
      </div>
    );
  }

  return (
    <div className='relative rounded-xl bg-white'>
      <header className='sticky top-0 z-10 flex items-center justify-between border-b border-gray-200 bg-white p-4'>
        <button
          onClick={() => navigate('/admin/jobs')}
          disabled={isBusy}
          className='flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-gray-900 disabled:opacity-50 disabled:cursor-not-allowed'
        >
          <ChevronLeftIcon className='h-5 w-5' />
          Go back
        </button>

        <div className='flex items-center gap-3'>
          <Button
            type='button'
            variant='secondary'
            disabled={isBusy}
            loading={isDraftSaving}
            onClick={() => {
              void handleSubmit((formData) => submitWithStatus(formData, 'DRAFT'))();
            }}
          >
            Save as Draft
          </Button>
          <Button
            type='button'
            disabled={isBusy}
            loading={isPublishing}
            onClick={() => {
              void handleSubmit((formData) => submitWithStatus(formData, 'PUBLISH'))();
            }}
          >
            Publish
          </Button>
        </div>
      </header>

      <div className='mx-auto max-w-6xl'>
        <div className='border-b border-gray-200 py-4'>
          <h1 className='mb-3 text-2xl font-bold text-gray-900'>{isEditMode ? 'Edit Job' : 'New Job'}</h1>
          <nav className='flex items-center gap-2 text-sm'>
            <button
              type='button'
              onClick={() => navigate('/admin/jobs')}
              className='text-wine-100 hover:text-wine-200 hover:underline disabled:opacity-50 disabled:cursor-not-allowed'
              disabled={isBusy}
            >
              All Jobs
            </button>
            {isEditMode ? (
              <>
                <span className='text-gray-400'>/</span>
                <button
                  type='button'
                  onClick={() => navigate(`/admin/jobs/${jobId}`)}
                  className='text-wine-100 hover:text-wine-200 hover:underline disabled:opacity-50 disabled:cursor-not-allowed'
                  disabled={isBusy}
                >
                  {(watchTitle || '').trim() || 'Job'}
                </button>
                <span className='text-gray-400'>/</span>
                <span className='text-gray-600'>Edit</span>
              </>
            ) : (
              <>
                <span className='text-gray-400'>/</span>
                <span className='text-gray-600'>New Job</span>
              </>
            )}
          </nav>
        </div>

        <div className='py-6 md:py-8'>
          <form onSubmit={(event) => event.preventDefault()} className='overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm'>
            <section className='border-b border-gray-200 p-4 md:p-6'>
              <SectionHeader icon={<FileText className='h-5 w-5' />} title='Basic Information' />

              <div className='grid gap-5 lg:grid-cols-2'>
                <Controller
                  name='jobTitle'
                  control={control}
                  render={({ field }) => (
                    <div>
                      <Input
                        label='Job Title'
                        required
                        placeholder='e.g. Senior Policy Analyst'
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        error={errors.jobTitle?.message}
                        disabled={isBusy}
                        $marginbottom='0'
                        $maxLength={120}
                        suffixIcon={<span className='text-xs text-gray-400'>{field.value.length}/120</span>}
                      />
                      {!errors.jobTitle ? <FieldHint>Use a clear, specific title to attract the right candidates.</FieldHint> : null}
                    </div>
                  )}
                />

                <Controller
                  name='jobLocation'
                  control={control}
                  render={({ field }) => (
                    <div>
                      <Input
                        label='Location'
                        required
                        placeholder='e.g. Johannesburg, South Africa'
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        error={errors.jobLocation?.message}
                        disabled={isBusy}
                        $marginbottom='0'
                        prefixIcon={<MapPin className='h-4 w-4 text-gray-400' />}
                      />
                      {!errors.jobLocation ? <FieldHint>City, state or country.</FieldHint> : null}
                    </div>
                  )}
                />

                <Controller
                  name='jobType'
                  control={control}
                  render={({ field }) => (
                    <div>
                      <CustomSelect
                        id='jobType'
                        label='Job Type'
                        required
                        name={field.name}
                        value={field.value}
                        onChange={(value) => field.onChange(Array.isArray(value) ? value[0] ?? '' : value)}
                        onBlur={() => field.onBlur()}
                        options={jobTypeOptions}
                        placeholder='Select job type'
                        disabled={isBusy}
                        error={errors.jobType?.message}
                        className=''
                      />
                      {!errors.jobType ? <FieldHint>e.g. Full-time, Contract, Internship</FieldHint> : null}
                    </div>
                  )}
                />
              </div>
            </section>

            <section className='border-b border-gray-200 p-4 md:p-6'>
              <SectionHeader icon={<Calendar1 className='h-5 w-5' />} title='Timeline' />

              <div className='grid gap-5 lg:grid-cols-2'>
                <Controller
                  name='startDate'
                  control={control}
                  render={({ field }) => (
                    <div>
                      <Calendar
                        label='Start Date'
                        required
                        selectedDate={parseDateOnly(field.value)}
                        onChange={(date) => {
                          field.onChange(date instanceof Date ? toDateOnly(date) : '');
                        }}
                        error={errors.startDate?.message}
                        $isDisabled={isBusy}
                        $marginbottom='0'
                        icon={<Calendar1 className='h-4 w-4' />}
                        minDate={new Date()}
                        placeholder='Select start date'
                        $dateFormat='yyyy-MM-dd'
                      />
                      {!errors.startDate ? <FieldHint>When will the role begin?</FieldHint> : null}
                    </div>
                  )}
                />

                <Controller
                  name='endDate'
                  control={control}
                  render={({ field }) => (
                    <div>
                      <Calendar
                        label='Application Deadline'
                        required
                        selectedDate={parseDateOnly(field.value)}
                        onChange={(date) => {
                          field.onChange(date instanceof Date ? toDateOnly(date) : '');
                        }}
                        error={errors.endDate?.message}
                        $isDisabled={isBusy}
                        $marginbottom='0'
                        icon={<Calendar1 className='h-4 w-4' />}
                        minDate={new Date()}
                        placeholder='Select deadline'
                        $dateFormat='yyyy-MM-dd'
                      />
                      {!errors.endDate ? <FieldHint>The last date candidates can apply.</FieldHint> : null}
                    </div>
                  )}
                />
              </div>
            </section>

            <section className='border-b border-gray-200 p-4 md:p-6'>
              <SectionHeader icon={<ListChecks className='h-5 w-5' />} title='Description' />
              <Controller
                name='description'
                control={control}
                render={({ field }) => (
                  <RichTextEditor
                    value={field.value || ''}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    disabled={isBusy}
                    placeholder='Type here....'
                    error={errors.description?.message}
                    className='border-gray-200'
                    toolbarContainerClassName='bg-gray-50 border-gray-200 px-4 py-2'
                    contentAreaClassName='min-h-[260px] max-h-[520px] p-3'
                    extendedToolbar={{ table: false }}
                  />
                )}
              />
            </section>

{/* This section is no longer needed */}
            {/* <section className='p-4 md:p-6'>
              <SectionHeader icon={<Mail className='h-5 w-5' />} title='Application Details' />

              <div className='max-w-xl'>
                <Controller
                  name='submissionEmail'
                  control={control}
                  render={({ field }) => (
                    <div>
                      <Input
                        label='Submission Email'
                        required
                        type='email'
                        placeholder='e.g. careers@afsa.org.za'
                        value={field.value}
                        onChange={field.onChange}
                        onBlur={field.onBlur}
                        error={errors.submissionEmail?.message}
                        disabled={isBusy}
                        $marginbottom='0'
                        prefixIcon={<Mail className='h-4 w-4 text-gray-400' />}
                      />
                      {!errors.submissionEmail ? <FieldHint>Applications will be sent to this email address.</FieldHint> : null}
                    </div>
                  )}
                />
              </div>
            </section> */}
          </form>
        </div>
      </div>
    </div>
  );
};

export default JobEditor;
